import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { Character } from '../Assets/Avatars';
import { useUser } from './UserContext';
import { api } from '../auth/api';

interface AvatarContextValue {
  character: Character | null;
  setCharacter: (c: Character) => void;
  clearCharacter: () => void;
  // True while we still don't know whether the account has a stored avatar
  // (no local pick yet AND the user summary hasn't loaded). The gate uses this
  // to avoid flashing the picker before the server avatar arrives on login.
  resolving: boolean;
}

const AvatarContext = createContext<AvatarContextValue | null>(null);

function storageKey(username: string): string {
  return `studybuddy:avatar:${username}`;
}

function readStored(username: string): Character | null {
  const raw = localStorage.getItem(storageKey(username));
  return raw === 'fizz' || raw === 'buzz' ? raw : null;
}

export const AvatarProvider: React.FC<{
  username: string;
  idToken?: string;
  children: React.ReactNode;
}> = ({ username, idToken, children }) => {
  // The account-bound avatar lives on user_summary.avatar and is the source of
  // truth. localStorage is a fast per-device cache so we don't flash the picker
  // before the summary loads.
  const { avatar: serverAvatar, summaryLoaded } = useUser();
  const [character, setCharacterState] = useState<Character | null>(() => readStored(username));

  // Adopt the account's stored avatar once it arrives, if this device has no
  // local pick. This is what makes the choice "only happen at signup" — a
  // returning user (even on a new device) gets their avatar back instead of
  // being re-prompted.
  useEffect(() => {
    if (!character && serverAvatar) {
      localStorage.setItem(storageKey(username), serverAvatar);
      setCharacterState(serverAvatar);
    }
  }, [character, serverAvatar, username]);

  const setCharacter = useCallback(
    (c: Character) => {
      localStorage.setItem(storageKey(username), c);
      setCharacterState(c);
      // Persist to the account so it sticks across devices/logins. Fire-and-
      // forget — the localStorage write already covers this session.
      if (idToken) {
        void api('/api/users/me/avatar', { method: 'PUT', token: idToken, body: { avatar: c } });
      }
    },
    [username, idToken],
  );

  const clearCharacter = useCallback(() => {
    localStorage.removeItem(storageKey(username));
    setCharacterState(null);
  }, [username]);

  const resolving = !character && !summaryLoaded;

  const value = useMemo<AvatarContextValue>(
    () => ({ character, setCharacter, clearCharacter, resolving }),
    [character, setCharacter, clearCharacter, resolving],
  );

  return <AvatarContext.Provider value={value}>{children}</AvatarContext.Provider>;
};

export function useAvatar(): AvatarContextValue {
  const ctx = useContext(AvatarContext);
  if (!ctx) throw new Error('useAvatar must be used within AvatarProvider');
  return ctx;
}
